/** @jsx tsx */
import { v, w, tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import * as _ from "lodash";
import {
	DEFAULT_TEXTBOX_PADDING,
	EXT_OFFSET,
	MINIMUM_IMAGE_PLACEHOLDER_WIDTH,
	PERCENTAGE_HUNDRED,
	TEXT_ALIGN,
	TEXT_STYLE_DEFAULTS,
} from "../../Constants";
import { firstCharacterStyles, getTextDimensions, roundNumber, xor } from "../../store/reducers/util";
import { CanvasMode, AutosizeMode } from "../interfaces/model/enumerations/index";
import { IShapeTextRectangle } from "../interfaces/model/geometry/ShapeTextRectangle";
import { ICanvasEditor, ITextStyles } from "../interfaces/model/index";
import { IShapeTextBody } from "../interfaces/model/text/ShapeTextbody";
import { getZoomValue, stateConstants } from "../selectors/index";
import { getDefaultFroalaHtml, removeLineBreak, removeZeroSpaceWidth } from "../util";
import { FroalaEditorWrapper } from "./FroalaEditorWrapper";
import SelectionHelper from "../../store/SelectionHelper";
import { IShape } from "../interfaces/model/PageObjects";
import sessionConstants from "csi/sessionConstants";

export interface IShapeTextBodyRenderProps {
	/**
	 * Is page object selected.
	 */
	isSelected?: boolean;

	/**
	 * Current canvas mode.
	 */
	canvasMode: CanvasMode;

	/** canvas editor object */
	canvasEditor: ICanvasEditor;

	/**
	 * Multiple shape selected flag
	 */
	isMultiSelect: boolean;

	/**
	 * Until we integrate paragraphs and runs with shape text body, keeping it optional
	 */
	textBody?: IShapeTextBody | undefined;

	/**
	 * Shape text rectangle bounding box
	 */
	textRect: IShapeTextRectangle;

	/**
	 * Selected shape height
	 */
	shapeHeight: number;

	/**
	 * Selected shape width
	 */
	shapeWidth: number;

	/**
	 * Flag for text shape
	 */
	isTextShape: boolean | undefined;

	/**
	 * Flag for table shape
	 */
	isTableShape: boolean | undefined;

	/**
	 * Selected shape shapeId
	 */
	shapeId: string;

	/**
	 * Key for shape group
	 */
	groupShapeKey?: string;

	/**
	 * Default Text Styles for shape
	 */
	defaultTextStyles?: ITextStyles;

	/**
	 * Page Object Properties
	 */
	pageObject: IShape;

	parentFlipH?: boolean;

	parentFlipV?: boolean;

	/** Page id of the selected page */
	selectedPageId: string;

	/**
	 * On select callback, used for selecting the shape.
	 */
	onSelect(event: PointerEvent): void;

	/**
	 * Pointer up event handling for dropping images here
	 */
	onPointerUp(event: PointerEvent): void;

	/**
	 * Double click event handingl to enable edit mode.
	 */
	onDblClick(event: MouseEvent): void;

	/**
	 * Callback to update model after content is changed.
	 */
	onContentChanged?(content: string, selectedPageNumber: number, transient?: boolean): void;

	/**
	 * Action call to set instance of text editor.
	 */
	setTextEditorInstance(textEditorInstance: any): void;

	/** Callback to set text first character styles */
	setFormatPainterTextStyles(textStyles: any, valueStyles: any): void;

	/** Set selected shape extends */
	setObjectExtends(extend: { cx?: number; cy?: number }, transient?: boolean): void;

	/** Deselect all objects callback function */
	deselectAllObjects?(): void;

	/**
	 * Select object id callback
	 * @param selectedObject Object Index in its parent child list
	 * @param isMultiSelect Flag to indicate multi selection
	 * @param transient Undo/redo stack flag
	 * @param forceSingleSelect Flag to indicate whether or not to force single select
	 */
	selectObjectById?(selectionId: string, isMultiSelect: boolean, transient: boolean, forceSingleSelect: boolean): void;

	/**
	 * Select object callback
	 * @param selectedObject Object index in its parent child list
	 * @param isMultiSelect Flag to indicate multi selection
	 * @param transient Undo/redo stack flag
	 * @param childId optionally select a subgroup element if selecting a group
	 */
	selectObject?(selectedObject: number, isMultiSelect?: boolean, transient?: boolean, childId?: number): void;

	/** Callback to set text first character styles */
	setFirstCharStyle(textStyles: any): void;

	/** Remove migrated extents callback function */
	removeMigratedExtents?(shapeIds: string[]): void;

	/**
	 * Double click event handingl to enable edit mode.
	 */
	onMouseEnter(event: MouseEvent): void;

	/**
	 * Double click event handingl to enable edit mode.
	 */
	onMouseLeave(event: MouseEvent): void;
}

/**
 * Shape text body stateless renderer
 */
export class ShapeTextBody extends WidgetBase<IShapeTextBodyRenderProps> {
	/**
	 * Froala editor instance
	 */
	private editorInstance: any;

	/**
	 * Handle to the Timer on Touch Start Event
	 * @type {any}
	 */
	private tapHoldTimerHandle: any = null;

	/**
	 * Flag to indicate whether Edit button is shown for touch devices on long press
	 */
	private showEditButton: boolean = false;

	/**
	 * Gets the text node
	 * @return {HTMLElement}
	 */
	protected getTextNode() {
		return SelectionHelper.getFroalaInstance();
	}

	/**
	 * On click function for froala editor wrapper, to focus back on the editor if user click on non froala
	 * element, which is current element.
	 */
	protected onFroalaEditorClick() {
		// Hide edit button if it is visible
		if (this.showEditButton === true) {
			this.showEditButton = false;
			this.invalidate();
		}

		// Do not execute for tables as it forces the cursor
		// to go to the last cell of the table.
		// Do not execute for tables as it forces the cursor
		// to go to the last cell of the table.
		if (this.editorInstance &&
			this.editorInstance.instance &&
			this.editorInstance.instance.editor
		) {
			const editor = this.editorInstance.instance.editor;
			editor.events.focus();
		}
	}

	/**
	 * Timer to detect Tap Hold
	 * @param {number} delay
	 */
	protected tapHoldTimer(delay: number) {
		this.tapHoldTimerHandle = setTimeout(() => {
			this.showEditButton = true;
			const mouseEvent: any = {};
			this.properties.onDblClick(mouseEvent);
			this.invalidate();
		}, delay);
	}

	/**
	 * Mutation observer on FroalaEditorWrapper will call this everytime FroalaEditor.el changes.
	 * @param mutationsList
	 */
	protected textboxResizer(mutationsList: any) {
		const { pageObject, setObjectExtends } = this.properties;
		const { autosize } = pageObject.spPr;
		const froalaEditor = this.getTextNode();
		const { el } = froalaEditor;
		const noText = !removeZeroSpaceWidth(removeLineBreak(el.innerText));
		for (const mutation of mutationsList) {
			if (mutation && setObjectExtends) {
				if (pageObject.isTextShape && autosize !== AutosizeMode.FIXED_SIZE) {
					if (autosize === AutosizeMode.FIXED_HEIGHT && !noText) {
						setObjectExtends({
							cx: getZoomValue(getTextDimensions(el).width) + DEFAULT_TEXTBOX_PADDING,
						}, true);
					}
					setObjectExtends({
						cy: el.offsetHeight + EXT_OFFSET,
					}, true);
				}

				// C8-25366 Issue (3)
				// Since the style of fr-box was set to "height: auto", and the container is now using flex,
				// the parent element #pageContent would scroll up on line return, this was a known bug
				// in another framework (Angular), and this work-around is an adaptation from github URL:
				// https://github.com/angular/material/issues/3070

				// This prevents scrolling of parent element "pageContent" for flex at center or flex-end on froala carriage return.
				const listEl = el.querySelector("ol,ul");
				let height = el.offsetHeight;
				if (listEl) {
					// we need to get the computed style since froala editor adds its own margin, it does not exist on the list element's style
					const computedStyle = getComputedStyle(listEl);
					const verticalMargin = parseFloat(computedStyle.marginTop) + parseFloat(computedStyle.marginBottom);
					height += verticalMargin;
				}
				el.parentElement.parentElement.style.height = `${height}px`;
			}
		}
	}

	/**
	 * Render shape text body
	 */
	protected render() {
		const {
			canvasEditor,
			canvasMode,
			defaultTextStyles,
			deselectAllObjects,
			groupShapeKey,
			isSelected,
			isTextShape,
			onContentChanged,
			onDblClick,
			onMouseEnter,
			onMouseLeave,
			onPointerUp,
			onSelect,
			parentFlipH,
			parentFlipV,
			pageObject,
			removeMigratedExtents,
			selectedPageId,
			selectObject,
			selectObjectById,
			setFirstCharStyle,
			setFormatPainterTextStyles,
			setObjectExtends,
			setTextEditorInstance,
			shapeHeight,
			shapeId,
			shapeWidth,
			textBody,
			textRect,
		} = this.properties;

		const { cx, cy } = pageObject.spPr.xfrm.ext;
		const verticalAlign = pageObject.spPr.verticalAlign;
		const autoWidth = pageObject.spPr.autosize === AutosizeMode.FIXED_HEIGHT;
		const isEditActive = (canvasMode === CanvasMode.EDIT_TEXT) && isSelected;
		const isEditNotActiveTextShape = isTextShape && !isEditActive;
		// merge default styles
		_.defaultsDeep(defaultTextStyles || {}, TEXT_STYLE_DEFAULTS);
		const dom = getDefaultFroalaHtml(defaultTextStyles as ITextStyles);
		const textContent = textBody ? textBody.content : dom.outerHTML;
		const toolbarType = {
			toolbarContainer: "#temp",
		};
		const isEditing = canvasMode == CanvasMode.EDIT_TEXT;
		const enablePointerEvents = isEditing || !canvasEditor?.isEditEnabled;

		// Check if shape has actual content or not
		const textDom = document.createElement("div") as HTMLElement;
		const cleanText = removeZeroSpaceWidth(textContent);
		textDom.innerHTML = cleanText;

		const innerTextNoLineBreaks = removeLineBreak((textDom.innerText || ""));
		const hasShapeText: boolean = (innerTextNoLineBreaks !== "");

		let textStyles: any;

		if (isSelected) {
			textStyles = firstCharacterStyles(cleanText);
			setFirstCharStyle(textStyles);
		}

		const bottom = PERCENTAGE_HUNDRED - (textRect.b as number * PERCENTAGE_HUNDRED) / cy;
		const left = (textRect.l as number * PERCENTAGE_HUNDRED) / cx;
		const right = PERCENTAGE_HUNDRED - (textRect.r as number * PERCENTAGE_HUNDRED) / cx;
		const top = (textRect.t as number * PERCENTAGE_HUNDRED) / cy;

		const nonTextAreaHeight = top + bottom;
		const nonTextAreaWidth = left + right;

		// Text area height inside shape
		const textRectHeight = (((PERCENTAGE_HUNDRED - nonTextAreaHeight)
			* shapeHeight) / PERCENTAGE_HUNDRED);

		// Text area width inside shape
		const textRectWidth = (((PERCENTAGE_HUNDRED - nonTextAreaWidth)
			* shapeWidth) / PERCENTAGE_HUNDRED);

		// froala editor inner wrapper styles
		const froalaInnerWrapperStyles: any = {
			cursor: `${isEditActive ? "text" : ""}`,
			display: "flex",
			flexDirection: "column",
			height: `${textRectHeight}px`,
			justifyContent: verticalAlign || TEXT_ALIGN.CENTER,
			minWidth: `${autoWidth ? "fit-content" : "inherit"}`,
			overflow: `${isEditNotActiveTextShape ? "hidden" : "inherit"}`,
			pointerEvents: enablePointerEvents ? "visible" : "none",
			whiteSpace: `${autoWidth ? "nowrap" : "normal"}`,
			width: `${textRectWidth}px`,
			wordWrap: `${autoWidth ? "normal" : "break-word"}`,
		};

		// If migrationExtents exists on object textBody, the shape was migrated from 6.2 and needs special handling
		// for rendering. Text overflow is allowed and is aligned to the center of the shape.
		if (textBody?.migrationExtents) {
			const { migrationExtents } = textBody;
			if (migrationExtents && (migrationExtents.cx > textRectWidth || migrationExtents.cy > textRectHeight)) {
				froalaInnerWrapperStyles.alignItems = "center";
				froalaInnerWrapperStyles.flexDirection = null;
				froalaInnerWrapperStyles.overflow = null;
				froalaInnerWrapperStyles.whiteSpace = "nowrap";
				froalaInnerWrapperStyles.wordWrap = null;
			}
		}

		let imagePlaceholderDimensions;

		if (pageObject.drawDimensions) {
			imagePlaceholderDimensions = "<br /><p style=\"width=100%;text-align: center;\">" +
				`${Math.round(shapeWidth)} x ${Math.round(shapeHeight)}</p>`;
		}

		// Text content dom if canvas mode is not edit mode and is text shape.
		const textContentDom = v("div", {
			classes: ["fr-element", "fr-view"],
			innerHTML: `${textContent}`,
		});

		// if mode is not EDIT_TEXT or the same text editor is still open, destroy froala instance if exist
		if (this.editorInstance && !isEditing) {
			this.editorInstance = undefined;
		}

		//Hide show edit button if shape is not selected.
		if (!isSelected) {
			this.showEditButton = false;
		}

		// Create froala instance only if mode is EDIT_TEXT and no froala instance exist
		if (!this.editorInstance && canvasMode === CanvasMode.EDIT_TEXT) {
			const migrationExtents = textBody && textBody.migrationExtents;
			this.editorInstance = w(FroalaEditorWrapper, {
				content: textContent,
				toolbarType,
				deselectAllObjects,
				selectObject,
				selectObjectById,
				groupShapeKey,
				// creating froala instance on shape selection(to add style to full text), so on shape selection,
				// with canvas mode is EDIT_TEXT and single shape selected then only initializing froala with edit mode.
				editOn: (canvasMode === CanvasMode.EDIT_TEXT) && isSelected,
				firstCharStyle: textStyles,
				isTextShape,
				onContentChanged,
				removeMigratedExtents,
				selectedPageId,
				selectedPageNumber: stateConstants().selectedPageNumber,
				setFirstCharStyle,
				setFormatPainterTextStyles,
				setObjectExtends,
				setTextEditorInstance,
				shapeId,
				shapeWidth,
				textRectHeight,
				textboxResizer: this.textboxResizer,
				migrationExtents,
			});
		} else if (canvasMode === CanvasMode.EDIT_TEXT) {
			if (
				this.editorInstance &&
				this.editorInstance.hasOwnProperty("properties") &&
				this.editorInstance.properties.hasOwnProperty("textRectHeight")
			) {
				this.editorInstance.properties.textRectHeight = textRectHeight;
			}
		}

		// Decide on flip text body along with shape.
		// IF flipH property is not defined then consider it to be false per model definition, being optional and,
		// defaulted to false
		let flipThisH = xor(pageObject.spPr.xfrm.flipH, !!textBody && !!textBody.flipH) ? true : false;
		flipThisH = xor(flipThisH, !!parentFlipH);
		let flipThisV = false;

		if (pageObject.imagePlaceholder) {
			flipThisV = xor(pageObject.spPr.xfrm.flipV, !!parentFlipV) ? true : false;
			flipThisH = xor(flipThisH, flipThisV);
		}
		const scale = `scale(${(flipThisH && !sessionConstants.isTextFlipHorizontalAllowed) ? -1 : 1}, ${flipThisV ? -1 : 1})`;
		// Note: This flip logic is changed in accordance with C8-5545 where text will flip along with shape when
		// flip is opted from menu. However, as per new requirement, text should not be flipped if the shape is flipped
		// by means of shape resize via mouse operation. (following MAC keynote shape flip behaviour)

		const froalaContainerStyles: Partial<CSSStyleDeclaration> = {
			bottom: `${bottom}%`,
			left: `${left}%`,
			right: `${right}%`,
			top: `${top}%`,
			pointerEvents: enablePointerEvents ? "visible" : "none",
			position: "absolute",
			transform: ` ${scale}`,
			userSelect: "none",
		};

		const editButtonStyles: Partial<CSSStyleDeclaration> = {
			display: "block",
			borderStyle: "none",
			borderRadius: "6px",
			borderWidth: "3px 4px 4px 3px",
			height: "30px",
			background: "#444",
			color: "#fff",
			lineHeight: "2",
			textAlign: "center",
			textDecoration: "none",
			fontWeight: "450",
			position: "absolute",
			top: `${top}%`,
			bottom: `${bottom}%`,
			left: `${left}%`,
			right: `${right}%`,
			transform: ` ${scale}`,
		};

		const editButtonDownArrowStyles: Partial<CSSStyleDeclaration> = {
			borderTop: "10px solid #444444",
			borderLeft: "15px solid transparent",
			borderRight: "15px solid transparent",
			bottom: "0px",
			content: "",
			position: "absolute",
			left: "50%",
			marginLeft: "-15px",
			width: "0",
			height: "0",
			top: "30px",
			right: `${right}%`,
			transform: ` ${scale}`,
		};

		// Text content dom if canvas mode is not in EDIT_TEXT
		const dimensionsDom = v("span", {
			innerHTML: `${imagePlaceholderDimensions}`,
		});

		// Restrict the width of the image placeholder description and dimension text
		// otherwise with smaller widths it displays in tall narrow vertical column
		if (pageObject.imagePlaceholder) {
			froalaInnerWrapperStyles["min-width"] = `${MINIMUM_IMAGE_PLACEHOLDER_WIDTH}px`;
		}

		const containerProperties = {
			key: "froala-container",
			styles: froalaContainerStyles,
			ontouchstart: () => { this.tapHoldTimer(1000); },
			ontouchmove: () => { clearTimeout(this.tapHoldTimerHandle); },
			ontouchend: () => { clearTimeout(this.tapHoldTimerHandle); },
			onmouseenter: onMouseEnter,
			onmouseleave: onMouseLeave,
			onpointerdown: onSelect,
			onpointerup: onPointerUp,
			ondblclick: onDblClick,
		};

		return (
			<div>
				{this.showEditButton == true ? (
					<div>
						<div key="edit-text-button" styles={editButtonStyles}>
							Edit
							<div key="edit-text-button-down-arrow" styles={editButtonDownArrowStyles} />
						</div>
					</div>
				) :
					<div />
				}
				<div {...containerProperties}>
					<div
						onclick={this.onFroalaEditorClick}
						styles={froalaInnerWrapperStyles}
					>
						{isEditActive ? this.editorInstance : (hasShapeText ? textContentDom : null)}
						{pageObject.drawDimensions ? dimensionsDom : null}
					</div>
				</div>
			</div>
		);
	}
}

export default ShapeTextBody;
